import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { ReactNode, useCallback } from "react";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { UserPreferences } from "src/model/user.ts";
import { UserState, authReducer } from "src/store/auth.ts";
import { Dialog, DialogSize, dialogReducer, hideDialog, showDialog } from "src/store/dialog.ts";
import { ToastMessage, ToastVariant, addToast, showToast, toastReducer } from "src/store/toast.ts";

const rootReducer = combineReducers({
  auth: authReducer,
  toast: toastReducer,
  dialog: dialogReducer,
});

export const setupStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    reducer: rootReducer,
    preloadedState: preloadedState,
  });
};

export const store = setupStore();

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useUserPreferences = (): UserPreferences | null => {
  const { preferences } = useAppSelector((state) => state.auth);
  if (preferences === undefined) {
    throw new Error("UserPreferences are not set");
  }
  return preferences;
};

export const useUserState = (): UserState => {
  const { user } = useAppSelector((state) => state.auth);
  if (user === null || user === undefined) {
    throw new Error("User is not set");
  }
  return user;
};

interface ToastHook {
  toastMessages: ToastMessage[];
  showToast: (toast: string, variant?: ToastVariant) => void;
}

export const useToasts = (): ToastHook => {
  const { toastMessages } = useAppSelector((state) => state.toast);
  const dispatch = useAppDispatch();

  const addToastMessage = useCallback(
    (toast: string, variant: ToastVariant = "primary") => {
      const id = uuidv4();
      dispatch(addToast({ message: toast, id: id, variant: variant }));
      setTimeout(() => dispatch(showToast(id)), 0);
    },
    [dispatch]
  );

  return {
    toastMessages: toastMessages,
    showToast: addToastMessage,
  };
};

interface DialogHook {
  dialog: Dialog | null;
  showDialog: (title: string, message: ReactNode, size?: DialogSize) => void;
  hideDialog: () => void;
}

export const useDialog = (): DialogHook => {
  const { dialog } = useAppSelector((state) => state.dialog);
  const dispatch = useAppDispatch();

  return {
    dialog: dialog,
    showDialog: (title: string, message: ReactNode, size: DialogSize = "lg") =>
      dispatch(showDialog({ title: title, message: message, size: size })),
    hideDialog: () => dispatch(hideDialog()),
  };
};
